import { Button, Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { HoverActions } from '@/components/form-elements/HoverActions';
import { FullHeightLayout } from '@/components/layouts/FullHeightLayout';
import { Action, IndicatorProcessService, IndicatorProcessStatus, Subject } from '@/services/api';
import { QueryKeys } from '@/services/QueryKeys';
import { FilterHeader } from '@/components/form-elements/FilterHeader';
import { useAppAbility } from '@/hooks/useAppAbility';
import { StatusChip } from '@/components/icons/StatusChip';
import { IndicatorProcessStatusIcon } from '@/components/icons/IndicatorProcessStatusIcon';

type FiltersFormData = { search: string };
const filtersDefaultValues: FiltersFormData = { search: '' };

export const IndicatorProcessesPage = () => {
  const [filters, setFilters] = useState<FiltersFormData>(filtersDefaultValues);
  const { t } = useTranslation();
  const ability = useAppAbility();

  const { data: indicatorProcesses = [], isLoading } = useQuery(
    QueryKeys.indicatorProcesses.allFilters(filters),
    () => IndicatorProcessService.findAll(filters),
    { keepPreviousData: false },
  );

  return (
    <FullHeightLayout>
      <Container>
        <FilterHeader<FiltersFormData>
          addButton={
            ability.can(Action.CREATE, Subject.INDICATOR_PROCESS) ? { label: 'Erstellen', to: 'erstellen' } : undefined
          }
          defaultValues={filtersDefaultValues}
          onChange={(formData) => setFilters(formData)}
          endChildren={
            <Button variant="contained" size="large" color="primary" component={Link} to="konfigurieren">
              Berechnungen
            </Button>
          }
        />

        <TableContainer>
          <Table aria-label="Anwendungsfälle">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Organisation</TableCell>
                <TableCell>Ankunftszeit von</TableCell>
                <TableCell>Ankunftszeit bis</TableCell>
                <TableCell align="right">Relationen</TableCell>
                <TableCell align="right">Geocodiert</TableCell>
                <TableCell align="right">Berechnet</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {indicatorProcesses.map(
                ({
                  name,
                  id,
                  organization,
                  arrivalSpanFrom,
                  arrivalSpanTo,
                  relationsTotalCount,
                  relationsGeocodingDoneCount,
                  relationsIndicatorsDoneCount,
                  status,
                }) => (
                  <TableRow key={id}>
                    <TableCell component="th" scope="row">
                      <HoverActions
                        title={name}
                        actions={[
                          { label: 'Bearbeiten', onClick: `/beratung/wsa-berechnung/${id}` },
                          ...([
                            IndicatorProcessStatus.CALCULATION_DONE,
                            IndicatorProcessStatus.INDICATORS_APPROVED,
                          ].includes(status)
                            ? [
                                {
                                  label: 'Analyse',
                                  onClick: `/organisation/${organization?.id}/wohnstandortanalyse/${id}`,
                                },
                              ]
                            : []),
                        ]}
                      />
                    </TableCell>
                    <TableCell>{organization?.name}</TableCell>
                    <TableCell>{arrivalSpanFrom && dayjs(arrivalSpanFrom).format('L LT')}</TableCell>
                    <TableCell>{arrivalSpanTo && dayjs(arrivalSpanTo).format('L LT')}</TableCell>
                    <TableCell align="right">{relationsTotalCount}</TableCell>
                    <TableCell align="right">{relationsGeocodingDoneCount}</TableCell>
                    <TableCell align="right">{relationsIndicatorsDoneCount}</TableCell>
                    <TableCell>
                      <StatusChip
                        icon={<IndicatorProcessStatusIcon status={status} />}
                        label={t(`IndicatorProcessStatus.${status}`)}
                      />
                    </TableCell>
                  </TableRow>
                ),
              )}

              {!isLoading && indicatorProcesses.length === 0 && (
                <TableRow>
                  <TableCell align="center" colSpan={8}>
                    Keine WSA Berechnungen gefunden
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </FullHeightLayout>
  );
};
